import type { ModalCloseReason } from "@/components/modal/modalCloseReason";
import { captureEvent } from "@/lib/analytics";
import { getContentCreationWizardAnalyticsContext } from "@/lib/contentCreationWizardAnalytics";

export type AiAssistentScope = "image" | "title" | "description";

export type AiAssistentOpenSource =
  | "photo_tile"
  | "photo_tile_bottom_sheet"
  | "photo_card"
  | "fullscreen_desktop_sidebar"
  | "fullscreen_mobile_edit_sheet"
  | "fullscreen_mobile_overflow_menu"
  | "version_gallery_menu"
  | "version_gallery_bottom_sheet"
  | "version_gallery_fullscreen_desktop"
  | "version_gallery_fullscreen_mobile_sheet"
  | "text_title_block"
  | "text_description_block"
  | "video_section"
  | "unknown";

export type AiAssistentCloseReason =
  | ModalCloseReason
  | "backdrop"
  | "scope_switch"
  | "programmatic"
  | "navigation_away";

export type AiAssistentGenerationOutcome = "success" | "error";

type ActiveSession = {
  id: string;
  openedAt: number;
  scope: AiAssistentScope;
  openSource: AiAssistentOpenSource;
  photoId: string | null;
  photoIndex: number | null;
  goodId: string | null;
  versionId: string | null;
  userMessageCount: number;
  generationAttempts: number;
  successfulGenerations: number;
  completed: boolean;
};

let activeSession: ActiveSession | null = null;

function createSessionId(): string {
  return `ai_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function wizardContext() {
  return getContentCreationWizardAnalyticsContext();
}

function sessionPayload(session: ActiveSession, extra?: Record<string, unknown>) {
  return {
    ai_session_id: session.id,
    scope: session.scope,
    open_source: session.openSource,
    photo_id: session.photoId,
    photo_index: session.photoIndex,
    good_id: session.goodId,
    version_id: session.versionId,
    user_message_count: session.userMessageCount,
    generation_attempts: session.generationAttempts,
    successful_generations: session.successfulGenerations,
    ...wizardContext(),
    ...extra,
  };
}

function finishSession(reason: AiAssistentCloseReason): void {
  const session = activeSession;
  if (!session || session.completed) return;

  session.completed = true;
  const timeOpenMs = Math.round(performance.now() - session.openedAt);

  captureEvent("[CardGenius] Закрытие AI-ассистента «Своя идея»", {
    ...sessionPayload(session),
    close_reason: reason,
    time_open_ms: timeOpenMs,
    had_successful_generation: session.successfulGenerations > 0,
    is_mobile_viewport: typeof window !== "undefined" && window.innerWidth < 640,
  });

  activeSession = null;
}

export function aiAssistentChatOpened(args: {
  scope: AiAssistentScope;
  openSource?: AiAssistentOpenSource;
  photoId?: string | null;
  photoIndex?: number | null;
  goodId?: string | null;
  versionId?: string | null;
}): void {
  if (activeSession && !activeSession.completed) {
    finishSession("scope_switch");
  }

  const openSource = args.openSource ?? "unknown";
  activeSession = {
    id: createSessionId(),
    openedAt: performance.now(),
    scope: args.scope,
    openSource,
    photoId: args.photoId ?? null,
    photoIndex: args.photoIndex ?? null,
    goodId: args.goodId ?? null,
    versionId: args.versionId ?? null,
    userMessageCount: 0,
    generationAttempts: 0,
    successfulGenerations: 0,
    completed: false,
  };

  if (args.scope === "image") {
    captureEvent("[CardGenius] Своя идея для фото", {
      ...sessionPayload(activeSession),
      marketplace: wizardContext()?.marketplace ?? null,
    });
    return;
  }

  captureEvent("[CardGenius] Своя идея для текста", {
    ...sessionPayload(activeSession),
    field: args.scope,
  });
}

export function aiAssistentChatClosed(reason: AiAssistentCloseReason = "programmatic"): void {
  finishSession(reason);
}

export function aiAssistentChatAbandonOnNavigationAway(): void {
  if (!activeSession || activeSession.completed) return;
  finishSession("navigation_away");
}

export function aiAssistentMessageSubmitted(args: {
  scope: AiAssistentScope;
  promptLength: number;
}): void {
  const session = activeSession;
  if (!session || session.completed) return;

  session.userMessageCount += 1;

  captureEvent("[CardGenius] Отправка в AI-ассистенте", {
    ...sessionPayload(session),
    scope: args.scope,
    prompt_length: args.promptLength,
    message_index: session.userMessageCount,
  });
}

export function aiAssistentGenerationBlocked(args: {
  scope: AiAssistentScope;
  requiredGeni: number;
}): void {
  const session = activeSession;
  if (!session || session.completed) return;

  captureEvent("[CardGenius] Недостаточно Geni для AI-ассистента", {
    ...sessionPayload(session),
    scope: args.scope,
    required_geni: args.requiredGeni,
  });
}

export function aiAssistentGenerationFinished(args: {
  scope: AiAssistentScope;
  outcome: AiAssistentGenerationOutcome;
  resultType: "image" | "text";
  promptLength: number;
  errorKind?: string;
}): void {
  const session = activeSession;
  if (!session || session.completed) return;

  session.generationAttempts += 1;
  if (args.outcome === "success") {
    session.successfulGenerations += 1;
  }

  captureEvent("[CardGenius] Результат генерации в AI-ассистенте", {
    ...sessionPayload(session),
    scope: args.scope,
    outcome: args.outcome,
    result_type: args.resultType,
    prompt_length: args.promptLength,
    ...(args.errorKind ? { error_kind: args.errorKind } : {}),
  });

  if (args.scope === "image" && args.outcome === "success" && session.photoId) {
    captureEvent("[CardGenius] Применён свой промпт к фото", {
      photo_id: session.photoId,
      prompt_length: args.promptLength,
      scope: mapOpenSourceToAppliedScope(session.openSource),
      marketplace: wizardContext()?.marketplace ?? null,
      ai_session_id: session.id,
      open_source: session.openSource,
    });
  }
}

function mapOpenSourceToAppliedScope(openSource: AiAssistentOpenSource): string {
  if (openSource.startsWith("fullscreen_")) return "fullscreen";
  if (openSource.startsWith("version_gallery_fullscreen_")) return "version_gallery_fullscreen";
  if (openSource.startsWith("version_gallery_")) return "version_gallery";
  return "ai_chat";
}

export function isAiAssistentAnalyticsActive(): boolean {
  return activeSession != null && !activeSession.completed;
}
